import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, ArrowRight, Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { getSession, updateReceiptData, shareSession } from "@/lib/api";
import { fmt, lineTotal } from "@/lib/calc";

const NumberField = ({ label, value, onChange, testid }) => (
  <div className="flex-1">
    <label className="text-[10px] uppercase tracking-[0.1em] font-bold text-muted-foreground">{label}</label>
    <input
      type="number"
      step="0.01"
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="mt-1 w-full bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 ring-ring font-mono"
    />
  </div>
);

export default function ReviewItems() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSession(code).then(setS).catch(() => toast.error("Sesiune negăsită"));
  }, [code]);

  if (!s) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const setField = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) =>
    setS((p) => {
      const items = [...p.items];
      items[i] = { ...items[i], [k]: v };
      return { ...p, items };
    });
  const addItem = () =>
    setS((p) => ({ ...p, items: [...p.items, { id: crypto.randomUUID(), name: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (i) => setS((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const itemsSubtotal = s.items.reduce((a, it) => a + lineTotal(it), 0);
  const computedTotal = itemsSubtotal + (s.service_fee || 0) + (s.tip || 0) - (s.discount || 0);

  const handleContinue = async () => {
    setSaving(true);
    try {
      await updateReceiptData(code, {
        restaurant_name: s.restaurant_name,
        date: s.date,
        currency: s.currency,
        items: s.items.map((it) => ({
          name: it.name || "Produs",
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
        })),
        service_fee: s.service_fee || 0,
        tip: s.tip || 0,
        discount: s.discount || 0,
        tax: s.tax || 0,
        stated_total: s.stated_total || computedTotal,
      });
      await shareSession(code);
      navigate(`/session/${code}/share`);
    } catch {
      toast.error("Nu am putut salva");
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      <Header subtitle="Verifică produsele" />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Pasul 3</span>
        <h1 className="font-display text-3xl font-black tracking-tight mt-2">Verifică și corectează</h1>
        <p className="text-muted-foreground mt-2">Modifică orice produs sau preț greșit înainte de a împărți nota.</p>

        <div className="mt-6 bg-card border border-border rounded-[24px] p-5 space-y-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Store size={16} />
            <span className="text-xs uppercase tracking-[0.15em] font-bold">Restaurant</span>
          </div>
          <input
            data-testid="restaurant-name-input"
            value={s.restaurant_name || ""}
            onChange={(e) => setField("restaurant_name", e.target.value)}
            placeholder="Nume restaurant"
            className="w-full bg-secondary rounded-xl px-3 py-2.5 outline-none focus:ring-2 ring-ring"
          />
          <input
            data-testid="receipt-date-input"
            value={s.date || ""}
            onChange={(e) => setField("date", e.target.value)}
            placeholder="Data (ex: 2026-06-14)"
            className="w-full bg-secondary rounded-xl px-3 py-2.5 outline-none focus:ring-2 ring-ring"
          />
        </div>

        <div className="mt-5 space-y-3" data-testid="items-list">
          {s.items.map((it, i) => (
            <motion.div
              key={it.id || i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-card border border-border rounded-[20px] p-4"
              data-testid={`item-row-${i}`}
            >
              <div className="flex items-center gap-2">
                <input
                  data-testid={`item-name-input-${i}`}
                  value={it.name}
                  onChange={(e) => setItem(i, "name", e.target.value)}
                  placeholder="Nume produs"
                  className="flex-1 bg-secondary rounded-xl px-3 py-2.5 font-medium outline-none focus:ring-2 ring-ring"
                />
                <button
                  data-testid={`remove-item-button-${i}`}
                  onClick={() => removeItem(i)}
                  className="w-10 h-10 shrink-0 rounded-xl bg-secondary flex items-center justify-center text-destructive active:scale-90 transition"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="flex gap-2 mt-2">
                <NumberField label="Cant." value={it.quantity} onChange={(v) => setItem(i, "quantity", v)} testid={`item-qty-input-${i}`} />
                <NumberField label="Preț/buc" value={it.unit_price} onChange={(v) => setItem(i, "unit_price", v)} testid={`item-price-input-${i}`} />
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-[0.1em] font-bold text-muted-foreground">Total linie</label>
                  <div className="mt-1 px-3 py-2.5 font-mono text-sm font-bold">{fmt(lineTotal(it), s.currency)}</div>
                </div>
              </div>
            </motion.div>
          ))}
          <button
            data-testid="add-item-button"
            onClick={addItem}
            className="w-full border border-dashed border-border rounded-[20px] py-3 font-semibold text-muted-foreground active:scale-[0.99] transition flex items-center justify-center gap-2"
          >
            <Plus size={18} /> Adaugă produs
          </button>
        </div>

        <div className="mt-5 bg-card border border-border rounded-[24px] p-5">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Taxe & extra</span>
          <div className="flex gap-2 mt-3">
            <NumberField label="Serviciu" value={s.service_fee || 0} onChange={(v) => setField("service_fee", v)} testid="service-fee-input" />
            <NumberField label="Bacșiș" value={s.tip || 0} onChange={(v) => setField("tip", v)} testid="tip-input" />
          </div>
          <div className="flex gap-2 mt-2">
            <NumberField label="TVA (inclus)" value={s.tax || 0} onChange={(v) => setField("tax", v)} testid="tax-input" />
            <NumberField label="Reducere" value={s.discount || 0} onChange={(v) => setField("discount", v)} testid="discount-input" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">TVA-ul e deja inclus în prețuri — se afișează doar informativ, nu se adaugă la total.</p>
          <div className="border-t border-border mt-4 pt-4 flex items-end justify-between">
            <span className="text-sm text-muted-foreground">Total calculat</span>
            <span data-testid="computed-total" className="font-display text-3xl font-black tracking-tight">{fmt(computedTotal, s.currency)}</span>
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 glass bg-background/80 border-t border-border p-4">
        <div className="max-w-2xl mx-auto">
          <button
            data-testid="continue-to-share-button"
            onClick={handleContinue}
            disabled={saving}
            className="w-full bg-primary text-primary-foreground rounded-full py-4 px-6 text-lg font-bold active:scale-[0.98] transition-transform flex justify-center items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : <>Generează link & QR <ArrowRight size={20} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
